import { useColorImage } from "../../data/useColorImages";
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Modal,
  Row,
  Col,
  Card,
  Image as RBImage,
  Form,
  Button
} from "react-bootstrap";
import { useLanguage } from "../helpers/LanguageContext";
import "../../App.css";
import {
  materialGroups,
  colorGroups,
  filterPaletteColors
} from "../components/colorPaletteGroups";
import MillingPickerModal from "./MillingPickerModal";
import PickerMaterial, { pickerMaterials } from "./PickerMaterial";
import PickerEmptyCard from "./PickerEmptyCard";
import PickerPagination, {
  usePickerPage,
  PICKER_PAGE_SIZE
} from "./PickerPagination";
import usePickerKeyboardNavigation from "./PickerKeyboardNavigation";

// ── Card ─────────────────────────────────────────
function ScrollingText({ text, active, max = 23 }) {
  const needsScroll = text.length > max;

  if (!needsScroll) {
    return (
      <div
        style={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          width: "100%",
          fontSize: "0.9rem"
        }}
      >
        <span>{text}</span>
      </div>
    );
  }

  return (
    <div
      style={{
        overflow: "hidden",
        whiteSpace: "nowrap",
        width: "100%",
        fontSize: "0.9rem",
        position: "relative"
      }}
    >
      <div
        style={{
          display: "flex",
          width: "max-content",
          animation: active
            ? `marquee ${Math.max(4, text.length * 0.25)}s linear infinite`
            : "none"
        }}
      >
        <span style={{ paddingRight: "1.5rem" }}>{text}</span>
        <span style={{ paddingRight: "1.5rem" }}>{text}</span>
      </div>

      {active && (
        <style>
          {`
          @keyframes marquee {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
        `}
        </style>
      )}
    </div>
  );
}

function HoverableColorCard({
  color,
  onSelect,
  onPreview,
  selected,
  showGroups
}) {
  const [hovered, setHovered] = useState(false);
  const { t } = useLanguage();
  const groups = colorGroups(color);

  return (
    <Card
      className={`h-100 p-2 ${selected ? "border-primary" : ""}`}
      data-picker-nav="item"
      data-picker-key={`color:${color.colorId}`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: "pointer",
        position: "relative",
        maxWidth: 220,
        margin: "0 auto",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 8px 20px rgba(0,0,0,0.15)"
          : "0 2px 6px rgba(0,0,0,0.08)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease"
      }}
      onClick={() => onSelect(color)}
    >
      {/* 🔴 FONTOS: wrapper div */}
      <div
        style={{
          overflow: "hidden",
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6
        }}
      >
        {color.imageDataReduced ? (
          <RBImage
            src={`data:image/jpeg;base64,${color.imageDataReduced}`}
            alt={t(color.name)}
            height={100}
            style={{
              objectFit: "cover",
              width: "100%",
              display: "block"
            }}
          />
        ) : (
          <div style={{ height: 100, background: color.hex || "#ddd" }} />
        )}
      </div>

      <Card.Body className="p-0 mt-2 text-center">
        <ScrollingText text={t(color.name)} active={hovered} />
        {showGroups && groups.length > 0 && (
          <div
            className="small text-muted text-truncate"
            title={groups.join(", ")}
          >
            {groups.join(", ")}
          </div>
        )}
      </Card.Body>
      <Button
        variant="link"
        size="sm"
        className="mt-auto"
        onClick={(event) => {
          event.stopPropagation();
          onPreview(color);
        }}
      >
        {t("preview")}
      </Button>
    </Card>
  );
}

// ── MAIN ─────────────────────────────────────────
export default function ColorPickerModal({
  show,
  onClose,
  onExited,
  onSelect,
  colors = [],
  millings = [],
  onSelectMilling,
  initialMode = "color",
  initialMaterial,
  colorId,
  millingId,
  millingVariantId = "",
  availableMillings = [],
  hasAssignment
}) {
  const { t } = useLanguage();

  const [pendingColor, setPendingColor] = useState(null);
  const [pendingMilling, setPendingMilling] = useState(null);
  const [millingMode, setMillingMode] = useState(initialMode === "milling");
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);
  const [previewColor, setPreviewColor] = useState(null);
  const fullPreviewColor = useColorImage(previewColor, show && !!previewColor);
  const [selectedGroup, setSelectedGroup] = useState("");

  const [selectedMaterial, setSelectedMaterial] = useState("");
  useEffect(() => {
    if (show) {
      setSelectedMaterial(initialMaterial || "");
      setPendingColor(
        colorId === undefined
          ? null
          : colors.find(
              (color) => String(color.colorId) === String(colorId)
            ) || {
              colorId
            }
      );
      setPendingMilling(null);
      setMillingMode(initialMode === "milling");
      setSearch("");
      setPreviewColor(null);
      setSelectedGroup("");
    }
  }, [show, initialMode, initialMaterial, colorId, colors]);
  const materials = useMemo(
    () => pickerMaterials([...colors, ...millings], selectedMaterial),
    [colors, millings, selectedMaterial]
  );
  const activeMaterial = materials.includes(selectedMaterial)
    ? selectedMaterial
    : materials[0] || "";
  const groups = useMemo(
    () => materialGroups(colors, activeMaterial),
    [colors, activeMaterial]
  );
  const activeGroup = groups.includes(selectedGroup) ? selectedGroup : "";
  const filteredColors = useMemo(
    () =>
      filterPaletteColors(colors, activeMaterial, activeGroup, search, t).sort(
        (a, b) =>
          t(a.name).localeCompare(t(b.name), undefined, {
            numeric: true,
            sensitivity: "base"
          })
      ),
    [colors, activeMaterial, activeGroup, search, t]
  );
  const initialColorIndex =
    colorId == null
      ? -1
      : filteredColors.findIndex(
          (color) => String(color.colorId) === String(colorId)
        );
  const initialPage =
    initialColorIndex < 0
      ? 1
      : Math.floor(initialColorIndex / (PICKER_PAGE_SIZE - 1)) + 1;
  const pagination = usePickerPage(
    filteredColors,
    JSON.stringify([show, search, activeMaterial, activeGroup]),
    PICKER_PAGE_SIZE - 1,
    initialPage
  );
  const handleTypeahead = (character) => {
    searchInputRef.current?.focus();
    setSearch((currentSearch) => currentSearch + character);
  };
  usePickerKeyboardNavigation(
    pagination,
    show && !millingMode && !previewColor,
    colorId === null ? "no-color" : colorId == null ? null : `color:${colorId}`,
    handleTypeahead
  );

  const closePicker = () => {
    setPendingColor(null);
    setPendingMilling(null);
    onClose();
  };
  const selectColor = (color) => setPendingColor(color);
  const confirm = (selection = pendingMilling) => {
    if (!millingMode && pendingColor?.colorId != null && onSelectMilling && !selection) {
      setMillingMode(true);
      return;
    }
    if (selection && onSelectMilling)
      onSelectMilling(selection.milling, selection.variantId, pendingColor);
    else if (pendingColor) onSelect(pendingColor);
    closePicker();
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Modal
        show={show && !millingMode}
        onExited={() => { if (!show) onExited?.(); }}
        onHide={closePicker}
        size="xl"
        centered
        backdrop
        container={document.body}
        style={{ zIndex: 2000 }}
        enforceFocus={!previewColor}
        keyboard={!previewColor}
      >
        <Modal.Header
          closeButton
          role="group"
          aria-label={t("chooseColor")}
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem"
          }}
        >
          <Modal.Title>{t("chooseColor")}</Modal.Title>
          <Button
            variant="outline-primary"
            disabled={!onSelectMilling}
            onClick={() => setMillingMode(true)}
          >
            {t("selectMilling")}
          </Button>

          <PickerMaterial
            materials={materials}
            value={activeMaterial}
            onChange={(material) => {
              setSelectedMaterial(material);
              setSelectedGroup("");
            }}
          />
          <Form.Control
            ref={searchInputRef}
            type="search"
            data-picker-search
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              marginLeft: "auto",
              width: 220,
              borderRadius: 999
            }}
          />
        </Modal.Header>

        <Modal.Body
          style={{
            height: "70vh",
            overflowY: "auto",
            background: "#fff"
          }}
        >
          <div
            className="mb-3 pb-1 w-100 d-flex align-items-center gap-3"
            style={{ overflowX: "auto", whiteSpace: "nowrap" }}
          >
            <div
              className="d-flex flex-shrink-0 gap-2"
              role="group"
              aria-label="Színcsoport"
              data-picker-nav-group="group"
            >
              <Button
                size="sm"
                variant={!activeGroup ? "primary" : "outline-primary"}
                aria-pressed={!activeGroup}
                data-picker-nav="group"
                onClick={() => setSelectedGroup("")}
              >
                {t("allGroups")}
              </Button>
              {groups.map((group) => (
                <Button
                  key={group}
                  size="sm"
                  variant={
                    activeGroup === group ? "primary" : "outline-primary"
                  }
                  aria-pressed={activeGroup === group}
                  data-picker-nav="group"
                  onClick={() => setSelectedGroup(group)}
                >
                  {group}
                </Button>
              ))}
            </div>
          </div>
          <Row
            xs={2}
            sm={3}
            md={4}
            lg={5}
            xl={6}
            className="g-3"
            data-picker-nav-group="item"
          >
            {[null, ...pagination.pageItems].map((c) => (
              <Col key={c?.colorId ?? "no-color"}>
                {c ? (
                  <HoverableColorCard
                    color={c}
                    showGroups={!activeGroup}
                    selected={
                      pendingColor
                        ? pendingColor.colorId === c.colorId
                        : colorId === c.colorId
                    }
                    onSelect={selectColor}
                    onPreview={setPreviewColor}
                  />
                ) : (
                  <PickerEmptyCard
                    label={t("noColor")}
                    pickerKey="no-color"
                    selected={
                      pendingColor
                        ? pendingColor.colorId == null
                        : colorId === null
                    }
                    onSelect={() => selectColor({ colorId: null })}
                  />
                )}
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {pagination.pageCount > 1 && (
            <div className="me-auto">
              <PickerPagination {...pagination} />
            </div>
          )}
          <Button variant="secondary" onClick={closePicker}>
            {t("cancel")}
          </Button>
          <Button
            disabled={!pendingColor && !pendingMilling}
            data-picker-confirm
            onClick={() => confirm()}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {show && millingMode && (
        <MillingPickerModal
          millings={millings}
          millingId={millingId}
          millingVariantId={millingVariantId}
          availableMillings={availableMillings}
          hasAssignment={hasAssignment}
          draftSelection={pendingMilling}
          canConfirm={!!pendingColor}
          initialMaterial={activeMaterial}
          onMaterialChange={(material) => {
            setSelectedMaterial(material);
            setPendingMilling(null);
          }}
          onSelect={(milling, variantId) => {
            if (milling !== undefined)
              onSelectMilling(milling, variantId, pendingColor);
            else if (pendingColor) onSelect(pendingColor);
          }}
          onClose={closePicker}
          onBack={(selection) => {
            setPendingMilling(selection);
            setMillingMode(false);
          }}
        />
      )}

      {/* ── PREVIEW ───────────────────────── */}
      <Modal
        show={!!previewColor}
        onHide={() => setPreviewColor(null)}
        size="xl"
        centered
        backdrop
        dialogClassName="color-picker-modal"
        container={document.body}
        style={{ zIndex: 2100 }}
      >
        {previewColor && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{t(previewColor.name)}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
              {fullPreviewColor?.imageError && <p role="alert">A teljes k?p bet?lt?se sikertelen.</p>}
              <RBImage
                src={`data:image/jpeg;base64,${fullPreviewColor?.imageData || previewColor.imageDataReduced || ""}`}
                fluid
                style={{ maxHeight: "60vh", objectFit: "contain" }}
              />
            </Modal.Body>
          </>
        )}
      </Modal>
    </div>
  );
}
